import {
  type Cancellation,
  useAuthFetch,
  useGuardedEffect,
} from "@p0security/frontend-core";
import type {
  BundleDefinition,
  BundleItem,
} from "@p0security/shared/types/bundle";
import type { Policy } from "@p0security/shared/types/policy/types";
import { message } from "@p0security/ui";
import { useCallback, useMemo, useState } from "react";
import type { UnifiedDoc } from "types/data";

export const MIN_BUNDLE_ITEMS = 2;
export const MAX_BUNDLE_ITEMS = 15;

export const bundleItemKey = (item: BundleItem): string => {
  const argParts = Object.entries(item.args)
    .sort(([a], [b]) => a.localeCompare(b))
    .flatMap(([, v]) => (Array.isArray(v) ? v.map(String) : [String(v)]));
  return [item.type, item.access, ...argParts].join(":");
};

export const toBundleId = (rule: Policy | undefined): string | undefined => {
  if (!rule) return undefined;
  const resource = rule.resource;
  if (
    resource?.type === "integration" &&
    resource.service === "bundle" &&
    resource.filters
  ) {
    const bundleFilter = (
      resource.filters as Record<
        string,
        { effect: string; key: string; pattern: string }
      >
    ).bundle;
    return bundleFilter?.pattern;
  }
  return undefined;
};

type StatusFilter = "all" | "disabled" | "enabled";

type BundleSavePayload = Pick<BundleDefinition, "items" | "label"> &
  Partial<
    Pick<
      BundleDefinition,
      "approval" | "description" | "disabled" | "requestor"
    >
  >;

type BundleResponse = { bundle: BundleDefinition };
type BundlesResponse = { bundles: BundleDefinition[] };

export const useAccessBundles = (statusFilter: StatusFilter = "all") => {
  const [bundles, setBundles] = useState<BundleDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const handleError = useCallback((error: string | undefined) => {
    if (error) message.error(error);
  }, []);
  const handleUnknownError = useCallback(
    (error: unknown) => message.error(String(error)),
    []
  );
  const authFetch = useAuthFetch(handleError);

  const load = useCallback(
    async (cancellation?: Cancellation) => {
      setLoading(true);
      try {
        const response = await authFetch("bundles", { method: "GET" });
        if (!response?.ok) return;

        const data: BundlesResponse = await response.json();
        if (cancellation?.isCancelled) return;
        setBundles(data.bundles);
      } finally {
        if (!cancellation?.isCancelled) setLoading(false);
      }
    },
    [authFetch]
  );

  useGuardedEffect(
    (cancellation) => async () => await load(cancellation),
    [load],
    handleUnknownError
  );

  const create = useCallback(
    async (bundle: BundleSavePayload) => {
      const response = await authFetch("bundles", {
        method: "POST",
        json: bundle,
      });
      if (!response?.ok) return undefined;

      const data: BundleResponse = await response.json();
      setBundles((current) => [data.bundle, ...current]);
      return data.bundle;
    },
    [authFetch]
  );

  const update = useCallback(
    async (id: string, patch: Partial<BundleSavePayload>) => {
      const existing = bundles.find((b) => b.id === id);
      if (!existing) return undefined;

      const merged = { ...existing, ...patch };
      const response = await authFetch(`bundles/${id}`, {
        method: "PUT",
        json: {
          approval: merged.approval,
          description: merged.description,
          disabled: merged.disabled,
          items: merged.items,
          label: merged.label,
          requestor: merged.requestor,
        },
      });
      if (!response?.ok) return undefined;

      const data: BundleResponse = await response.json();
      setBundles((current) =>
        current.map((b) => (b.id === id ? data.bundle : b))
      );
      return data.bundle;
    },
    [authFetch, bundles]
  );

  const remove = useCallback(
    async (id: string) => {
      const response = await authFetch(`bundles/${id}`, { method: "DELETE" });
      if (!response?.ok) return;

      setBundles((current) => current.filter((b) => b.id !== id));
    },
    [authFetch]
  );

  const setDisabled = useCallback(
    async (id: string, disabled: boolean) => {
      await update(id, { disabled });
    },
    [update]
  );

  const list = useMemo((): {
    docs: UnifiedDoc<BundleDefinition>[];
    loading: boolean;
  } => {
    let filtered = bundles;
    if (statusFilter === "enabled")
      filtered = bundles.filter((b) => !b.disabled);
    if (statusFilter === "disabled")
      filtered = bundles.filter((b) => !!b.disabled);
    return { docs: filtered.map((b) => ({ id: b.id, data: b })), loading };
  }, [bundles, loading, statusFilter]);

  return { list, create, update, remove, setDisabled };
};

export const useAccessBundle = (bundleId: string | undefined) => {
  const [bundle, setBundle] = useState<BundleDefinition | undefined>();
  const [loading, setLoading] = useState(!!bundleId);
  const [error, setError] = useState<string | undefined>();
  const handleError = useCallback((err: string | undefined) => {
    if (err) setError(err);
  }, []);
  const handleUnknownError = useCallback(
    (err: unknown) => setError(String(err)),
    []
  );
  const authFetch = useAuthFetch(handleError);

  useGuardedEffect(
    (cancellation) => async () => {
      if (!bundleId) {
        setBundle(undefined);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const response = await authFetch(`bundles/${bundleId}`, {
          method: "GET",
        });
        if (!response?.ok || cancellation.isCancelled) return;
        const data: BundleResponse = await response.json();
        if (!cancellation.isCancelled) setBundle(data.bundle);
      } finally {
        if (!cancellation.isCancelled) setLoading(false);
      }
    },
    [bundleId, authFetch],
    handleUnknownError
  );

  const clearError = useCallback(() => setError(undefined), []);

  const save = useCallback(
    async (payload: BundleSavePayload) => {
      setError(undefined);
      const body: Record<string, unknown> = {
        label: payload.label,
        items: payload.items,
      };
      if (payload.requestor) body.requestor = payload.requestor;
      if (payload.approval) body.approval = payload.approval;
      if (payload.disabled !== undefined) body.disabled = payload.disabled;
      // The editor doesn't edit description, and PUT replaces the whole doc,
      // so carry over the loaded one to keep API/Terraform descriptions
      const description = payload.description ?? bundle?.description;
      if (description !== undefined) body.description = description;
      const response = bundleId
        ? await authFetch(`bundles/${bundleId}`, { method: "PUT", json: body })
        : await authFetch("bundles", { method: "POST", json: body });
      if (!response?.ok) return undefined;

      const data: BundleResponse = await response.json();
      setBundle(data.bundle);
      return data.bundle;
    },
    [bundleId, bundle, authFetch]
  );

  return { bundle, loading, error, clearError, save };
};
